import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Input, Button, notification } from "antd";
import NotFoundPage from "./404_error/NotFoundPage";
import Error404 from "./404_error/expiredlink";
import LoadingWave from "./Loader";
import PDFViewer from "./viewers/PDFViewer";
import DOCXViewer from "./viewers/DOCXViewer";
import VideoViewer from "./viewers/VideoViewer";
import PPTXViewer from "./viewers/PPTXViewer";
import WebPageViewer from "./viewers/WebPageViewer";
import { useUser } from "../context/Usercontext";
import close_icon from './viewers/close_icon.svg'
import success_icon from './viewers/success_icon.svg';
import close_mark from './viewers/close_mark.svg';

// JWT Security Helper
// JWT Security Helper - Updated to handle encoding issues
const JWT_SECURITY = {
  verifyToken: async (token, expectedAction) => {
    if (!token) {
      throw new Error('No token provided');
    }

    try {
      // Decode JWT payload (client-side verification for tampering detection)
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      
      // Verify action matches expected
      if (payload.action !== expectedAction) {
        throw new Error('Token action mismatch');
      }
      
      // Verify timestamp is recent (within 15 minutes)
      const tokenAge = Date.now() - payload.timestamp;
      if (tokenAge > 15 * 60 * 1000) {
        throw new Error('Token timestamp expired');
      }
      
      // Verify token has required fields
      if (!payload.nonce || !payload.checksum) {
        throw new Error('Token missing security fields');
      }
      
      // Additional client-side integrity checks
      const requiredFields = ['timestamp', 'nonce', 'checksum', 'action'];
      for (const field of requiredFields) {
        if (!payload[field]) {
          throw new Error(`Token missing required field: ${field}`);
        }
      }
      
      return payload;
    } catch (error) {
      throw new Error(`Token verification failed: ${error.message}`);
    }
  },

  // Helper function to normalize strings for comparison (handles encoding issues)
  normalizeString: (str) => {
    if (typeof str !== 'string') return str;
    
    // Normalize common encoding issues
// Normalize common encoding issues
return str
  // Handle various em dash encodings
  .replace(/â€"/g, '—')
  .replace(/â/g, '—')
  .replace(/â€™/g, "'")
  .replace(/â€œ/g, '"')
  .replace(/â€/g, '"')
  .replace(/\s+/g, ' ')
  .trim();
  },

  // Compare values with encoding normalization
  compareValues: (val1, val2, fieldName) => {
    // Handle null/undefined cases
    if (val1 == null && val2 == null) return true;
    if (val1 == null || val2 == null) return false;
    
    // Handle string comparison with normalization
    if (typeof val1 === 'string' && typeof val2 === 'string') {
      const normalized1 = JWT_SECURITY.normalizeString(val1);
      const normalized2 = JWT_SECURITY.normalizeString(val2);
      
      if (normalized1 !== normalized2) {
        console.warn(`String comparison failed for ${fieldName}:`);
        console.warn(`Response: "${val1}" (normalized: "${normalized1}")`);
        console.warn(`Token: "${val2}" (normalized: "${normalized2}")`);
        
        // For fileName specifically, be more lenient
        if (fieldName === 'fileName') {
          // Check if they're substantially the same (ignoring encoding issues)
          const base1 = normalized1.replace(/[^\w\s.-]/g, '');
          const base2 = normalized2.replace(/[^\w\s.-]/g, '');
          return base1 === base2;
        }
        
        return false;
      }
      return true;
    }
    
    // Handle object comparison
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }
    
    // Direct comparison for other types
    return val1 === val2;
  },

  // Secure API request with token verification
  secureApiRequest: async (url, body, expectedAction) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      
      const data = await response.json();
      
      // Always verify token exists
      if (!data.token) {
        throw new Error('Server response missing security token');
      }
      
      // Verify token integrity
      const tokenPayload = await JWT_SECURITY.verifyToken(data.token, expectedAction);
      
      // Cross-verify response data with token payload using improved comparison
      const responseKeys = Object.keys(data).filter(key => key !== 'token');
      for (const key of responseKeys) {
        if (tokenPayload[key] !== undefined) {
          if (!JWT_SECURITY.compareValues(data[key], tokenPayload[key], key)) {
            throw new Error(`Response data mismatch for field: ${key}`);
          }
        }
      }
      
      return { ...data, verified: true, tokenPayload };
    } catch (error) {
      console.error('Secure API request failed:', error);
      throw error;
    }
  }
};

const Viewer = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [decryptedData, setDecryptedData] = useState(null);
  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  
  // approved emails
  const [approvedEmail, setApprovedEmail] = useState("");
  const [approvedOtp, setApprovedOtp] = useState("");
  const [approvedStep, setApprovedStep] = useState("email");
  
  const [isValidated, setIsValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ip, location, userId, region, os, device, browser } = useUser();

  const [notificationApi, notificationHolder] = notification.useNotification();

  // Security notification helper
  const showSecurityError = (message) => {
    notificationApi.error({
      message: "Security Error",
      description: `${message}. Please refresh and try again.`,
      placement: "topRight",
      duration: 10,
      icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
      closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
    });
  };

  // Fetch document config with JWT security
  useEffect(() => {
    // Check for missing or placeholder context values
    const invalidValues = ["Detecting...", "Generating...", null, undefined];
    const contextValues = { userId, ip, location, region, os, device, browser };
    const hasInvalid = Object.values(contextValues).some(value =>
      invalidValues.includes(value)
    );

    if (!id || hasInvalid) {
      console.warn("Waiting for full context to load:", contextValues);
      return; // Don't proceed if any value is still being detected
    }

  const fetchDocument = async () => {
  try {
    const result = await JWT_SECURITY.secureApiRequest(
      `https://user-view-backend.vercel.app/api/shortid/viewer/${id}`,
      { userId, ip, location, region, os, device, browser },
      'GET_DOCUMENT'
    );

    if (!result.verified) {
      throw new Error('Response verification failed');
    }

    setData(result);
    
    // Decrypt token and set decrypted data
    try {
      const decrypted = await JWT_SECURITY.verifyToken(result.token, 'GET_DOCUMENT');
      
      // Check if the JWT payload indicates document not found
      if (decrypted.success === false && decrypted.message === "Document not found") {
        setError("Document not found");
        return;
      }
      
      setDecryptedData(decrypted);
      console.log("Decrypted token data:", decrypted);
    } catch (decryptError) {
      console.error('Token decryption failed:', decryptError);
      showSecurityError("Token decryption failed");
    }
  } catch (err) {
    console.error('Document fetch error:', err);
    if (err.message.includes('security') || err.message.includes('token')) {
      showSecurityError("Document fetch failed with security error");
    } else {
      setError("Document not found"); // Changed from "Not found" to "Document not found"
    }
  }
};

    fetchDocument();
  }, [id, userId, ip, location, region, os, device, browser]);

  // Determine access state and show appropriate modal
  useEffect(() => {
    if (!data || !decryptedData) return;
    
    // Use decrypted data for navigation decisions
    const documentData = decryptedData;
    
    // Check if document is deactivated
    if (documentData.deactivateLink) {
      return setError("Document deactivated");
    }

    // Check existing access
    const hasPasswordAccess = documentData.hasPasswordAccess;
    const hasApprovedEmailAccess = documentData.hasApprovedEmailAccess;

    // If user already has any required access, allow viewing
    if (documentData.passwordProtect && hasPasswordAccess) {
      setIsValidated(true);
      return;
    }
    if (documentData.approvedEmail && hasApprovedEmailAccess) {
      setIsValidated(true);
      return;
    }

    // Show appropriate modal based on protection type
    if (documentData.passwordProtect && !hasPasswordAccess) {
      setShowPasswordModal(true);
    } else if (documentData.approvedEmail && !hasApprovedEmailAccess) {
      setShowApprovedModal(true);
    } else {
      // No protection or user has access
      setIsValidated(true);
    }
  }, [data, decryptedData]);

  // Secure Password handling
  const handlePasswordSubmit = async () => {
    if (!passwordInput.trim()) {
      return notificationApi.error({
        message: "Error: Password Required",
        description: "Please enter the password.",
        placement: "topRight",
        icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
        closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
      });
    }
    
    setLoading(true);
    try {
      const result = await JWT_SECURITY.secureApiRequest(
        `https://user-view-backend.vercel.app/api/validate-password`,
        { 
          shortId: id, 
          password: passwordInput,
          userId,
          ip, location, region, os, device, browser
        },
        'VALIDATE_PASSWORD'
      );
      
      if (!result.verified) {
        throw new Error('Response verification failed');
      }
      
      if (result.validpassword && result.tokenPayload.validpassword) {
        notificationApi.success({ 
          message: "Success", 
          description: "Password verified securely",
          placement: "topRight",
          icon: <img src={success_icon} alt="Success Icon" style={{ width: 24, height: 24 }} />,
          closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
        });
        setShowPasswordModal(false);
        setIsValidated(true);
      } else {
        notificationApi.error({ 
          message: "Invalid Password", 
          description: result.message || "Incorrect password.",
          placement: "topRight",
          icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
          closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
        });
      }
    } catch (err) {
      console.error('Password validation error:', err);
      showSecurityError("Password validation failed with security error");
    } finally {
      setLoading(false);
    }
  };

  // Secure Approved emails handling
  const handleApprovedEmailSubmit = async () => {
    if (!/\S+@\S+\.\S+/.test(approvedEmail)) {
      return notificationApi.error({ 
        message: "Invalid Email",
        description: "Please enter a valid email address.",
        placement: "topRight",
        icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
        closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
      });
    }
    
    setLoading(true);
    try {
      const result = await JWT_SECURITY.secureApiRequest(
        `https://user-view-backend.vercel.app/api/validate/approved/send-otp`,
        { 
          email: approvedEmail, 
          documentId: id,
          userId,
          ip, location, region, os, device, browser
        },
        'SEND_APPROVED_OTP'
      );
      
      if (!result.verified) {
        throw new Error('Response verification failed');
      }
      
      if (result.otpSent && result.tokenPayload.otpSent) {
        if (result.hasAccess) {
          // User already has access
          notificationApi.success({ 
            message: "Access Granted", 
            description: "You already have access to this document." ,
            placement: "topRight",
            icon: <img src={success_icon} alt="Success Icon" style={{ width: 24, height: 24 }} />,
            closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
          });
          setShowApprovedModal(false);
          setIsValidated(true);
        } else {
          notificationApi.success({ 
            message: "OTP Sent", 
            description: "OTP sent to your approved email securely.",
            placement: "topRight",
            icon: <img src={success_icon} alt="Success Icon" style={{ width: 24, height: 24 }} />,
            closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
          });
          setApprovedStep("otp");
        }
      } else {
        notificationApi.error({ 
          message: "Access Denied",
          description: result.message || "Email not approved for access",
          placement: "topRight",
          icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
          closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
        });
      }
    } catch (err) {
      console.error('Approved email submit error:', err);
      showSecurityError("Approved email submission failed with security error");
    } finally { 
      setLoading(false); 
    }
  };

  const handleApprovedOtpSubmit = async () => {
    if (!approvedOtp.trim()) {
      return notificationApi.error({ 
        message: "Missing OTP",
        description: "Please enter the OTP sent to your email.",
        placement: "topRight",
        icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
        closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
      });
    }
    
    setLoading(true);
    try {
      const result = await JWT_SECURITY.secureApiRequest(
        `https://user-view-backend.vercel.app/api/validate/approved/validate-otp`,
        { 
          email: approvedEmail, 
          otp: approvedOtp,
          userId,
          documentId: id,
          ip, location, region, os, device, browser
        },
        'VALIDATE_APPROVED_OTP'
      );
      
      if (!result.verified) {
        throw new Error('Response verification failed');
      }
      
      if (result.approved && result.tokenPayload.approved) {
        notificationApi.success({ 
          message: "Access Approved", 
          description: "Document access granted successfully.",
          placement: "topRight",
          icon: <img src={success_icon} alt="Success Icon" style={{ width: 24, height: 24 }} />,
          closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
        });
        setShowApprovedModal(false);
        setIsValidated(true);
      } else {
        notificationApi.error({ 
          message: "Invalid OTP",
          description: result.message || "OTP verification failed",
          placement: "topRight",
          icon: <img src={close_icon} alt="Error Icon" style={{ width: 24, height: 24 }} />,
          closeIcon: <img src={close_mark} alt="Close Icon" style={{ width: 16, height: 16 }} />
        });
      }
    } catch (err) {
      console.error('Approved OTP submit error:', err);
      showSecurityError("OTP verification failed with security error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press for modals
  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  // Render logic
if (error === "Document deactivated" || error === "Document not found") return <Error404 />;
  if (!data || !decryptedData) return <LoadingWave />;
  if (decryptedData.deactivateLink) return <Error404 />;

  if (!isValidated) {
    return (
      <>
        {notificationHolder}
        
        {/* Password Modal */}
        {showPasswordModal && (
          <Modal
            title="🔐 Password Protected Document"
            visible
            okText="Verify Password"
            confirmLoading={loading}
            onOk={handlePasswordSubmit}
            cancelButtonProps={{ style: { display: "none" } }}
            closable={false}
            maskClosable={false}
          >
            <p>This document requires a password to view.</p>
            <Input.Password
              placeholder="Enter password"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              onKeyPress={e => handleKeyPress(e, handlePasswordSubmit)}
              autoFocus
            />
          </Modal>
        )}

        {/* Approved Email Modal */}
        {showApprovedModal && (
          <Modal
            title={approvedStep === "email" ? "✅ Approved Email Access" : "🔢 Enter Verification Code"}
            visible
            confirmLoading={loading}
            closable={false}
            maskClosable={false}
            footer={[
              <Button
                key="submit"
                type="primary"
                loading={loading}
                onClick={approvedStep === "email" ? handleApprovedEmailSubmit : handleApprovedOtpSubmit}
                style={{ width: "100%" }}
              >
                {approvedStep === "email" ? "Send Verification Code" : "Verify & Access Document"}
              </Button>
            ]}
          >
            {approvedStep === "email" ? (
              <>
                <p>This document requires an approved email address:</p>
                <Input 
                  placeholder="Enter your approved email address" 
                  type="email"
                  value={approvedEmail} 
                  onChange={e => setApprovedEmail(e.target.value)}
                  onKeyPress={e => handleKeyPress(e, handleApprovedEmailSubmit)}
                  autoFocus
                />
              </>
            ) : (
              <>
                <p>Enter the verification code sent to <strong>{approvedEmail}</strong>:</p>
                <Input 
                  placeholder="Enter 6-digit verification code" 
                  value={approvedOtp} 
                  onChange={e => setApprovedOtp(e.target.value)}
                  onKeyPress={e => handleKeyPress(e, handleApprovedOtpSubmit)}
                  maxLength={6}
                  autoFocus
                />
              </>
            )}
          </Modal>
        )}
      </>
    );
  }

  // Document viewer rendering - using decrypted data
  const { 
    mimeType, 
    originalUrl, 
    totalPages, 
    customLogoUrl, 
    downloadEnabled,
    leadCaptureConfig,
    aiAgentEnabled,  // ✅ NOW EXTRACTED FROM DECRYPTED DATA
    shortId,
    fileName,
    plantype
  } = decryptedData;

  console.log("Using decrypted data:", {
    mimeType, 
    originalUrl, 
    totalPages, 
    customLogoUrl, 
    downloadEnabled,
    leadCaptureConfig,
    aiAgentEnabled,   // ✅ NOW LOGGED
    shortId,
    fileName,
    plantype
  });

  const commonProps = { 
    url: originalUrl, 
    mimeType,
    documentId: id,  // Pass document ID for AI agent
    userId,          // Pass user ID for AI agent
    fileName,        // Pass filename for AI agent context
    plantype         // Pass plan type for feature availability
  };
  
  // Add logo if enabled
  if (customLogoUrl) {
    commonProps.logoUrl = customLogoUrl;
  }

  // Add download if enabled
  if (downloadEnabled) {
    commonProps.downloadEnabled = downloadEnabled;
  }

  // Add lead capture config if available
  if (leadCaptureConfig) {
    // Pass either delaySeconds or scrollPercent based on what's available
    if (leadCaptureConfig.delaySeconds) {
      commonProps.delaySeconds = leadCaptureConfig.delaySeconds;
    }
    if (leadCaptureConfig.scrollPercent) {
      commonProps.scrollPercent = leadCaptureConfig.scrollPercent;
    }
  }

  // ✅ ADD AI AGENT CONFIGURATION
  if (aiAgentEnabled) {
    commonProps.aiAgentEnabled = true;
    commonProps.aiAgentConfig = {
      documentId: id,
      userId,
      fileName,
      mimeType,
      plantype,
      // Add any additional AI agent configuration here
      features: {
        chat: true,
        summarize: true,
        search: true,
        translate: plantype === 'pro' || plantype === 'enterprise',
        analysis: plantype === 'enterprise'
      }
    };
    console.log("AI Agent enabled with config:", commonProps.aiAgentConfig);
  }

  // Render appropriate viewer based on MIME type
  if (mimeType.includes("video")) {
    return <VideoViewer {...commonProps} mimeType={mimeType.split("/")[0]} />;
  }
  if (mimeType.includes("pdf")) {
    return <PDFViewer {...commonProps} totalPages={totalPages} />;
  }
  if (
    mimeType === "application/msword" ||
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return <DOCXViewer {...commonProps} totalPages={totalPages} />;
  }
  if (mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
    return <PPTXViewer {...commonProps} />;
  }
  
  // Default to web page viewer
  return <WebPageViewer {...commonProps} />;
};

export default Viewer;
